import { SUPPORTED_LOCALES } from "@zplanem/translations"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

export const LanguageSwitcher: React.FC = () => {
	const { locale, setLocale } = useLocale()
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const t = getTranslations(locale)

	useEffect(() => {
		if (!open) return

		const handlePointerDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false)
			}
		}

		const handleKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false)
		}

		document.addEventListener("mousedown", handlePointerDown)
		document.addEventListener("keydown", handleKey)
		return () => {
			document.removeEventListener("mousedown", handlePointerDown)
			document.removeEventListener("keydown", handleKey)
		}
	}, [open])

	return (
		<div ref={containerRef} className="relative">
			<Button
				type="button"
				variant="secondary"
				size="sm"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={t.language.label}
				onClick={() => setOpen((prev) => !prev)}
				className="h-8 px-3 text-xs font-bold bg-muted dark:bg-muted/60 backdrop-blur-md border border-border text-foreground hover:bg-accent rounded-xl flex items-center gap-1.5"
			>
				<span>{locale.toUpperCase()}</span>
				<svg
					className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
					viewBox="0 0 12 12"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="M3 4.5L6 7.5L9 4.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</Button>

			{open && (
				<div
					role="listbox"
					aria-label={t.language.label}
					className="absolute right-0 mt-2 min-w-36 bg-popover text-popover-foreground backdrop-blur-md border border-border rounded-xl p-1 shadow-xl z-50"
				>
					{SUPPORTED_LOCALES.map((loc) => {
						const isActive = loc === locale
						return (
							<button
								key={loc}
								type="button"
								role="option"
								aria-selected={isActive}
								onClick={() => {
									setLocale(loc)
									setOpen(false)
								}}
								className={`w-full text-left px-3 py-2 text-xs font-medium rounded-lg flex items-center justify-between gap-3 transition-colors ${
									isActive
										? "bg-primary text-primary-foreground hover:bg-primary/90"
										: "text-foreground/80 hover:text-foreground hover:bg-accent"
								}`}
							>
								<span>{t.language[loc]}</span>
								<span className="text-[10px] font-bold opacity-70">
									{loc.toUpperCase()}
								</span>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}
